import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { isActiveCarpoolLine, resolveStudentCarpoolLine, sortCarpoolLinesForFallback } from '@car-pool/shared/carpoolLines';
import { apiFetch, SOCKET_URL } from '../config/api';
import { buildDismissalConsoleData, buildDismissalDashboardData } from '../lib/dismissalData';
import { buildSchoolGradeOptions } from '../lib/schoolGradeOptions';

function getGradeLabelMap(gradeNames = []) {
  return Object.fromEntries(
    gradeNames
      .map((grade) => [Number(grade.grade_code), grade.label])
      .filter(([gradeCode]) => !Number.isNaN(gradeCode))
  );
}

const EMPTY_DATA = {
  school: null,
  students: [],
  contacts: [],
  staff: [],
  dismissalStatuses: [],
  geofenceEvents: [],
  gradeNames: [],
  campuses: [],
  carpoolLines: [],
  lineAssignments: [],
};

export default function useSchoolDismissalLiveData({ schoolId, token, enabled = true }) {
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState(EMPTY_DATA);
  const [clockNow, setClockNow] = useState(() => new Date());
  const debounceRef = useRef(null);
  const connectedRef = useRef(false);

  const loadLiveData = useCallback(async () => {
    if (!enabled || !schoolId || !token) return;

    const [dismissalRes, geofenceRes] = await Promise.allSettled([
      apiFetch(`/dismissal-status?school_id=${schoolId}`, {}, token),
      apiFetch(`/geofenced-data/school/${schoolId}/current`, {}, token),
    ]);

    setRawData((current) => ({
      ...current,
      dismissalStatuses: dismissalRes.status === 'fulfilled'
        ? dismissalRes.value.data || []
        : current.dismissalStatuses,
      geofenceEvents: geofenceRes.status === 'fulfilled'
        ? geofenceRes.value.data || []
        : current.geofenceEvents,
    }));
  }, [enabled, schoolId, token]);

  const loadData = useCallback(async () => {
    if (!enabled || !schoolId || !token) {
      setRawData(EMPTY_DATA);
      setLoading(false);
      return;
    }

    try {
      const [
        schoolRes,
        studentsRes,
        contactsRes,
        staffRes,
        dismissalRes,
        geofenceRes,
        gradeNamesRes,
        campusesRes,
        carpoolLinesRes,
        lineAssignmentsRes,
      ] = await Promise.allSettled([
        apiFetch(`/schools/${schoolId}`, {}, token),
        // Fetch the full roster (not just status=active). The dashboard builder
        // filters to active students internally for stats/pickup/lanes, but it
        // needs inactive students too so a parent whose children were deactivated
        // still counts as matched (no false "no assigned children" alert).
        apiFetch(`/students?school_id=${schoolId}`, {}, token),
        apiFetch(`/contacts?school_id=${schoolId}`, {}, token),
        apiFetch(`/staff?school_id=${schoolId}`, {}, token),
        apiFetch(`/dismissal-status?school_id=${schoolId}`, {}, token),
        apiFetch(`/geofenced-data/school/${schoolId}/current`, {}, token),
        apiFetch(`/school-grade-names?school_id=${schoolId}`, {}, token),
        apiFetch(`/campuses/school/${schoolId}`, {}, token),
        apiFetch('/carpool-lines', {}, token),
        apiFetch('/student-line-assigned/active', {}, token),
      ]);

      const students = studentsRes.status === 'fulfilled' ? studentsRes.value.data || [] : [];
      const configuredGradeNames = gradeNamesRes.status === 'fulfilled' ? gradeNamesRes.value.data || [] : [];

      setRawData({
        school: schoolRes.status === 'fulfilled' ? schoolRes.value.data || null : null,
        students,
        contacts: contactsRes.status === 'fulfilled' ? contactsRes.value.data || [] : [],
        staff: staffRes.status === 'fulfilled' ? staffRes.value.data || [] : [],
        dismissalStatuses: dismissalRes.status === 'fulfilled' ? dismissalRes.value.data || [] : [],
        geofenceEvents: geofenceRes.status === 'fulfilled' ? geofenceRes.value.data || [] : [],
        gradeNames: buildSchoolGradeOptions(configuredGradeNames, students),
        campuses: campusesRes.status === 'fulfilled' ? campusesRes.value.data || [] : [],
        carpoolLines: carpoolLinesRes.status === 'fulfilled' ? carpoolLinesRes.value.data || [] : [],
        lineAssignments: lineAssignmentsRes.status === 'fulfilled' ? lineAssignmentsRes.value.data || [] : [],
      });
    } finally {
      setLoading(false);
    }
  }, [enabled, schoolId, token]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!enabled) return undefined;

    const intervalId = setInterval(() => {
      setClockNow(new Date());
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !schoolId) return undefined;

    const socket = io(SOCKET_URL);
    const scheduleLiveRefresh = () => {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = window.setTimeout(() => {
        loadLiveData();
      }, 250);
    };

    socket.on('connect', () => {
      socket.emit('watch-school', schoolId);
      if (connectedRef.current) loadLiveData();
      connectedRef.current = true;
    });
    socket.on('dismissal-status-changed', (payload = {}) => {
      if (payload.school_id && Number(payload.school_id) !== Number(schoolId)) return;
      const studentId = Number(payload.student_id);
      if (!studentId || !payload.status) {
        scheduleLiveRefresh();
        return;
      }

      setRawData((current) => {
        const index = current.dismissalStatuses.findIndex(
          (status) => Number(status.student_id) === studentId
        );
        if (index === -1) {
          return {
            ...current,
            dismissalStatuses: [...current.dismissalStatuses, payload],
          };
        }

        const dismissalStatuses = [...current.dismissalStatuses];
        dismissalStatuses[index] = { ...dismissalStatuses[index], ...payload };
        return { ...current, dismissalStatuses };
      });
    });
    socket.on('geofence-event', (payload = {}) => {
      if (payload.school_id && Number(payload.school_id) !== Number(schoolId)) return;
      const contactId = Number(payload.parent_driver_id);
      const studentId = Number(payload.student_id);
      const hasScope = contactId || studentId;
      if (!hasScope || !payload.id || !payload.timestamp || !payload.action) {
        scheduleLiveRefresh();
        return;
      }

      setRawData((current) => {
        const isSameScope = (event) => (
          contactId
            ? Number(event.parent_driver_id) === contactId
            : (!event.parent_driver_id && Number(event.student_id) === studentId)
        );
        return {
          ...current,
          geofenceEvents: [
            payload,
            ...current.geofenceEvents.filter((event) => !isSameScope(event)),
          ],
        };
      });
    });

    return () => {
      window.clearTimeout(debounceRef.current);
      connectedRef.current = false;
      socket.disconnect();
    };
  }, [enabled, loadLiveData, schoolId]);

  const gradeLabelMap = useMemo(
    () => getGradeLabelMap(rawData.gradeNames),
    [rawData.gradeNames]
  );

  const { lanes, laneByStudentId } = useMemo(() => {
    const campusIds = new Set((rawData.campuses || []).map((campus) => Number(campus.campus_id)));
    const schoolLines = sortCarpoolLinesForFallback(
      (rawData.carpoolLines || []).filter((line) => campusIds.has(Number(line.campus_id)) && isActiveCarpoolLine(line))
    );
    const assignmentByStudentId = new Map(
      (rawData.lineAssignments || []).map((assignment) => [Number(assignment.student_id), assignment])
    );

    const laneMap = {};
    (rawData.students || []).forEach((student) => {
      if (student?.status === 'inactive') return;
      const { line } = resolveStudentCarpoolLine({
        student,
        explicitAssignment: assignmentByStudentId.get(Number(student.id)) || null,
        lines: schoolLines,
        groupingMode: rawData.school?.student_grouping_mode,
      });
      if (line) laneMap[Number(student.id)] = Number(line.carpool_line_id);
    });

    return {
      lanes: schoolLines.map((line) => ({
        id: Number(line.carpool_line_id),
        name: line.name,
        line_type: line.line_type,
      })),
      laneByStudentId: laneMap,
    };
  }, [rawData.campuses, rawData.carpoolLines, rawData.lineAssignments, rawData.school, rawData.students]);

  const [liveLineIds, setLiveLineIds] = useState(null);
  // The whole resolved schedule, not just which lines are live: the dashboard
  // countdown needs every line's time to find the next one still ahead.
  const [activeSchedule, setActiveSchedule] = useState(null);

  useEffect(() => {
    if (!schoolId || !token) {
      setLiveLineIds(null);
      setActiveSchedule(null);
      return undefined;
    }

    let cancelled = false;
    const load = () => {
      apiFetch(`/carpool-schedule/active?school_id=${schoolId}`, {}, token)
        .then((res) => {
          if (cancelled) return;
          setLiveLineIds(new Set((res.data?.live_line_ids || []).map(Number)));
          setActiveSchedule(res.data || null);
        })
        // Leaving this null means "liveness unknown", and every line is shown.
        .catch(() => {
          if (cancelled) return;
          setLiveLineIds(null);
          setActiveSchedule(null);
        });
    };

    load();
    // Windows open and close on the minute; this is cheap and cached server-side.
    const interval = window.setInterval(load, 60000);
    return () => { cancelled = true; window.clearInterval(interval); };
  }, [schoolId, token]);

  const dashboardData = useMemo(
    () => buildDismissalDashboardData({
      lanes,
      laneByStudentId,
      liveLineIds,
      students: rawData.students,
      contacts: rawData.contacts,
      staff: rawData.staff,
      dismissalStatuses: rawData.dismissalStatuses,
      geofenceEvents: rawData.geofenceEvents,
      gradeLabelMap,
      now: clockNow,
    }),
    [clockNow, gradeLabelMap, lanes, laneByStudentId, liveLineIds, rawData]
  );

  const consoleData = useMemo(
    () => buildDismissalConsoleData({
      students: rawData.students,
      contacts: rawData.contacts,
      dismissalStatuses: rawData.dismissalStatuses,
      geofenceEvents: rawData.geofenceEvents,
      gradeLabelMap,
      laneByStudentId,
      now: clockNow,
    }),
    [clockNow, gradeLabelMap, laneByStudentId, rawData]
  );

  return {
    loading,
    activeSchedule,
    refresh: loadData,
    refreshLiveData: loadLiveData,
    students: rawData.students,
    contacts: rawData.contacts,
    parents: rawData.contacts.filter((contact) => ['parent', 'guardian'].includes(String(contact?.type || '').toLowerCase())),
    staff: rawData.staff,
    dismissalStatuses: rawData.dismissalStatuses,
    geofenceEvents: rawData.geofenceEvents,
    gradeNames: rawData.gradeNames,
    gradeLabelMap,
    consoleLanes: lanes,
    consoleLaneByStudentId: laneByStudentId,
    ...dashboardData,
    ...consoleData,
  };
}
